<template>
  <div class="whss-early-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ $t("Early Year Progress Report") }}</h4>
        <b-badge
          v-if="!loading && reportStudents.length"
          variant="light-success"
          class="ml-2"
        >
          {{ reportStudents.length }} {{ $t("Student(s)") }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>{{ $t("Print Report") }}</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>{{ $t("Close") }}</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="state-panel">
      <b-spinner variant="primary" />
      <p class="mt-2 mb-0">{{ $t("Loading report…") }}</p>
    </div>

    <div v-else-if="error" class="state-panel state-panel--error">
      <p class="text-danger mb-2">{{ error }}</p>
      <button class="btn-modern btn-print" @click="loadReportData">
        {{ $t("Retry") }}
      </button>
    </div>

    <div v-else class="report-stack">
      <div
        v-for="(student, studentIndex) in displayStudents"
        :key="'whss-eyr-' + (student.sID || studentIndex)"
        class="report-sheet"
      >
        <div class="report-sheet__frame">
          <div class="report-sheet__body">
        <header class="letterhead">
          <div class="letterhead__logo-wrap">
            <img
              :src="schoolLogo"
              alt="WHSS"
              class="letterhead__logo"
            />
          </div>
          <div class="letterhead__titles">
            <div class="letterhead__term-box">
              {{ reportExamBanner }}
            </div>
            <div
              v-if="reportClassBanner"
              class="letterhead__class-box"
            >
              {{ reportClassBanner }}
            </div>
            <p v-if="reportSessionLine" class="letterhead__session">
              {{ reportSessionLine }}
            </p>
          </div>
        </header>

        <table class="student-info-table">
          <colgroup>
            <col class="student-info-table__col-label" />
            <col class="student-info-table__col-main" />
            <col class="student-info-table__col-main" />
            <col class="student-info-table__col-roll-label" />
            <col class="student-info-table__col-roll" />
          </colgroup>
          <tbody>
            <tr>
              <td class="student-info-table__label">
                {{ $t("Student's Name") }}:
              </td>
              <td class="student-info-table__name" colspan="2">
                <span class="student-info-table__box student-info-table__box--name">{{
                  val(student.name)
                }}</span>
              </td>
              <td class="student-info-table__label student-info-table__label--roll">
                {{ $t("Roll No") }}:
              </td>
              <td class="student-info-table__roll">
                <span class="student-info-table__box student-info-table__box--roll">{{
                  val(student.grNo || student.sID)
                }}</span>
              </td>
            </tr>
            <tr>
              <td class="student-info-table__label">
                {{ $t("Class Average Age") }}:
              </td>
              <td class="student-info-table__age" colspan="3">
                <span class="student-info-table__age-group">
                  <span class="student-info-table__age-label">{{ $t("Years") }}:</span>
                  <span class="student-info-table__mini">{{
                    classAverageAge.years
                  }}</span>
                  <span class="student-info-table__age-label">{{ $t("Months") }}:</span>
                  <span class="student-info-table__mini">{{
                    classAverageAge.months
                  }}</span>
                </span>
              </td>
              <td
                class="student-info-table__signature"
                rowspan="2"
              >
                <div class="student-info-table__signature-line"></div>
              </td>
            </tr>
            <tr>
              <td class="student-info-table__label">
                {{ $t("Student's Age") }}:
              </td>
              <td class="student-info-table__age" colspan="3">
                <span class="student-info-table__age-group">
                  <span class="student-info-table__age-label">{{ $t("Years") }}:</span>
                  <span class="student-info-table__mini">{{
                    student.ageYears
                  }}</span>
                  <span class="student-info-table__age-label">{{ $t("Months") }}:</span>
                  <span class="student-info-table__mini">{{
                    student.ageMonths
                  }}</span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="report-body">
          <div class="subjects-wrap subjects-wrap--full">
            <div
              v-for="(column, colIndex) in balanceCategoriesIntoColumns(
                student.assessment.allCategories
              )"
              :key="'col-' + student.sID + '-' + colIndex"
              class="subjects-wrap__col"
            >
              <div
                v-for="category in column"
                :key="'cat-' + student.sID + '-' + category.key"
                class="subject-block"
              >
                <table class="subject-table">
                  <thead>
                    <tr>
                      <th colspan="2" class="subject-table__title">
                        {{ category.title }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, idx) in category.items"
                      :key="category.key + '-r-' + idx"
                      :class="{
                        'subject-table__section-row': item.isSectionLabel,
                      }"
                    >
                      <template v-if="item.isSectionLabel">
                        <td colspan="2" class="subject-table__section-label">
                          {{ item.optionName }}
                        </td>
                      </template>
                      <template v-else>
                        <td class="subject-table__criteria">
                          {{ item.optionName }}
                        </td>
                        <td class="subject-table__mark">
                          {{ formatMark(item.rating) }}
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="report-sheet__footer">
          <table class="grading-key-footer">
            <thead>
              <tr>
                <th colspan="8" class="grading-key-footer__heading">
                  {{ $t("GRADING KEY") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  v-for="item in gradeLegend"
                  :key="'gk-' + item.code"
                  colspan="2"
                  class="grading-key-footer__item"
                >
                  <span class="grading-key-footer__code">{{ item.code }}</span>
                  <span class="grading-key-footer__label">{{ item.label }}</span>
                </td>
              </tr>
            </tbody>
          </table>

        <section class="comments">
          <div class="comments__label">
            {{ $t("Class Teacher's Comments") }}:
          </div>
          <div class="comments__body">
            <p class="comments__text">
              {{ student.teacherComment || " " }}
            </p>
          </div>
        </section>

        <footer class="signatures">
          <div class="signatures__cell">
            <div class="signatures__line"></div>
            <div class="signatures__label">{{ $t("Class Teacher") }}</div>
          </div>
          <div class="signatures__cell">
            <div class="signatures__line"></div>
            <div class="signatures__label">{{ $t("Section Head") }}</div>
          </div>
          <div class="signatures__cell">
            <div class="signatures__line"></div>
            <div class="signatures__label">{{ $t("Principal") }}</div>
          </div>
        </footer>
        </div>
          </div>
        </div>
      </div>

      <div v-if="!displayStudents.length" class="state-panel">
        <p class="mb-0">{{ $t("No student records found") }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

const WHSS_GRADE_LEGEND = [
  { code: "E", label: "Excellent" },
  { code: "G", label: "Good" },
  { code: "S", label: "Satisfactory" },
  { code: "NI", label: "Needs Improvement" },
];

const WHSS_REPORT_LOGO =
  "https://cdn.cloudious.net/file-1793699390420-313747288.png";

function parseDob(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  const s = String(raw).trim();
  if (!s) return null;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const dmy = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function agePartsFromDob(dob, refDate = new Date()) {
  const birth = parseDob(dob);
  if (!birth) return { years: "—", months: "—", totalMonths: null };
  let years = refDate.getFullYear() - birth.getFullYear();
  let months = refDate.getMonth() - birth.getMonth();
  if (refDate.getDate() < birth.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return { years: "—", months: "—", totalMonths: null };
  return {
    years: String(years),
    months: String(months),
    totalMonths: years * 12 + months,
  };
}

function averageAgeFromTotalMonths(totalMonthsList) {
  const valid = totalMonthsList.filter((n) => Number.isFinite(n));
  if (!valid.length) return { years: "—", months: "—" };
  const avg = Math.round(
    valid.reduce((a, b) => a + b, 0) / valid.length
  );
  return {
    years: String(Math.floor(avg / 12)),
    months: String(avg % 12),
  };
}

export default {
  name: "EarlyYearReportWhss",
  components: { BSpinner, BBadge },
  data() {
    return {
      loading: false,
      error: null,
      reportStudents: [],
      dobByStudentId: {},
      campusName: "Wahaj Hussain's School System",
      schoolLogo: WHSS_REPORT_LOGO,
      reportMeta: {
        className: "",
        sectionName: "",
        examTypeName: "",
        sessionName: "",
      },
      gradeLegend: WHSS_GRADE_LEGEND,
    };
  },
  computed: {
    reportExamBanner() {
      const raw = String(
        this.reportMeta.examTypeName || this.$t("Progress Report")
      )
        .replace(/\*+/g, "")
        .trim();
      const exam = raw.toUpperCase();
      if (exam.includes("PROGRESS REPORT")) {
        return exam;
      }
      return `${exam} PROGRESS REPORT`.replace(/\s+/g, " ").trim();
    },
    reportClassBanner() {
      return String(this.reportMeta.className || "").trim();
    },
    reportSessionLine() {
      const session = String(this.reportMeta.sessionName || "").trim();
      return session ? `Session ${session}` : "";
    },
    classAverageAge() {
      const totals = this.reportStudents.map((s) => {
        const dob =
          s.dateOfBirth ||
          s.dob ||
          this.dobByStudentId[String(s.sID)] ||
          null;
        return agePartsFromDob(dob).totalMonths;
      });
      return averageAgeFromTotalMonths(totals);
    },
    displayStudents() {
      return this.reportStudents.map((student) => {
        const dob =
          student.dateOfBirth ||
          student.dob ||
          this.dobByStudentId[String(student.sID)] ||
          null;
        const age = agePartsFromDob(dob);
        return {
          sID: student.sID,
          name: student.name,
          grNo: student.grNo,
          ageYears: age.years,
          ageMonths: age.months,
          teacherComment: this.resolveTeacherComment(student),
          assessment: this.buildStudentAssessment(student),
        };
      });
    },
  },
  created() {
    this.initMetaFromQuery();
    this.loadCampusInfo();
    this.loadReportData();
  },
  methods: {
    initMetaFromQuery() {
      const q = this.$route.query;
      this.reportMeta = {
        className: q.clsName || "",
        sectionName: q.secName || "",
        examTypeName: q.examTypeName || "",
        sessionName: q.sessionName || "",
      };
    },

    async loadCampusInfo() {
      const db = this.$route.query.db || this.$store.state.userData.db;
      const cID = this.$route.query.cID || this.$store.state.userData.cId;
      const token = this.$store.state.userData.token;

      this.schoolLogo = WHSS_REPORT_LOGO;

      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db, cId: cID },
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find(
            (c) => String(c.id) === String(cID)
          );
          if (match) {
            this.campusName = match.name || this.campusName;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => String(b.id) === String(cID)) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.name ||
            currentBranch.branch ||
            this.campusName;
        }
      }
    },

    async loadStudentDobs() {
      try {
        const q = this.$route.query;
        const db = q.db || this.$store.state.userData.db;
        const cID = q.cID || this.$store.state.userData.cId;
        const classID = q.classID || 0;
        const sectionID = q.sectionID || 0;

        const res = await axios.get(
          `${this.$store.state.domain}students/Filterdata?cls=${classID}&sec=${sectionID}&status=present&db=${db}&cId=${cID}&page=1`,
          {
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
            },
          }
        );

        let list = [];
        if (res.data && res.data.status === "success") {
          list = Array.isArray(res.data.data)
            ? res.data.data
            : (res.data.data && res.data.data.data) || [];
        } else if (Array.isArray(res.data)) {
          list = res.data;
        }

        const map = {};
        list.forEach((s) => {
          const id = String(s.id || s.sID || "");
          if (!id) return;
          map[id] =
            s.dateOfBirth ||
            s.dob ||
            s.birthDate ||
            s.date_of_birth ||
            null;
        });
        this.dobByStudentId = map;
      } catch (e) {
        console.warn("WHSS early year: DOB fetch failed", e);
      }
    },

    async loadReportData() {
      const q = this.$route.query;
      const db = q.db || this.$store.state.userData.db;
      const cID = q.cID || this.$store.state.userData.cId;
      const classID = q.classID;
      const sectionID = q.sectionID;
      const examTypeID = q.examTypeID;
      const sessionID = q.sessionID;

      if (!classID || !sectionID || !examTypeID || !sessionID) {
        this.error = this.$t(
          "Missing report filters. Please open this report from Print Result."
        );
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        await this.loadStudentDobs();

        const response = await axios.get(
          `${this.$store.state.domain}TabulationSheetMarks/GetStudentWiseByFilters`,
          {
            params: {
              db,
              cID,
              classID,
              sectionID,
              examTypeID,
              sessionID,
            },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        const payload = response.data;
        let rows = [];
        if (
          payload &&
          payload.status === "success" &&
          Array.isArray(payload.data)
        ) {
          rows = payload.data;
        } else if (Array.isArray(payload)) {
          rows = payload;
        } else if (payload && Array.isArray(payload.data)) {
          rows = payload.data;
        }

        const sIDs = String(q.sIDs || "")
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean);

        if (sIDs.length) {
          rows = rows.filter((row) => sIDs.includes(String(row.sID)));
        }

        this.reportStudents = rows;
      } catch (e) {
        console.error("WHSS early year report load error:", e);
        this.error =
          (e.response && e.response.data && e.response.data.message) ||
          this.$t("Failed to load report.");
      } finally {
        this.loading = false;
      }
    },

    resolveTeacherComment(student) {
      const existing = student.teacherComments || student.comments;
      if (Array.isArray(existing) && existing.length) {
        return existing
          .map((line) => String(line || "").trim())
          .filter(Boolean)
          .join(" ");
      }
      if (typeof existing === "string" && existing.trim()) {
        return existing.trim();
      }

      const remarks = student.generalRemarks;
      if (typeof remarks === "string" && remarks.trim()) {
        return remarks.trim();
      }

      const remarkObj =
        remarks && typeof remarks === "object" && !Array.isArray(remarks)
          ? remarks
          : {};

      const fromRecord = (student.records || [])
        .map(
          (record) =>
            record.remarks ||
            (record.marks && record.marks.remarks) ||
            ""
        )
        .map((text) => String(text || "").trim())
        .find(Boolean);

      return String(
        remarkObj.grade ||
          remarkObj.meta_sub_grade ||
          remarkObj.remarks ||
          fromRecord ||
          student.teacherComment ||
          ""
      ).trim();
    },

    whssBlockTitle(record, subcategoryNames) {
      const subject = String(record.subjectName || "")
        .replace(/^[\s,]+/, "")
        .trim();
      if (subcategoryNames.length > 1) {
        return subject || subcategoryNames[0] || "OTHER";
      }
      const onlySub = subcategoryNames[0] || "";
      if (!subject) return onlySub || "OTHER";
      const subLower = onlySub.toLowerCase();
      const subjLower = subject.toLowerCase();
      if (
        onlySub &&
        subLower !== subjLower &&
        !subLower.includes(subjLower) &&
        !subjLower.includes(subLower)
      ) {
        return onlySub;
      }
      return subject;
    },

    buildStudentAssessment(student) {
      const records = (student.records || [])
        .slice()
        .sort(
          (a, b) =>
            (Number(a.sortIndex) || 0) - (Number(b.sortIndex) || 0) ||
            (Number(a.subjectID) || 0) - (Number(b.subjectID) || 0)
        );

      const allCategories = [];

      records.forEach((record, recordIndex) => {
        const marksData = (record.marksData || []).slice();
        if (!marksData.length) return;

        const subMap = {};
        marksData.forEach((item) => {
          const subName = String(item.categoryName || "GENERAL").trim() || "GENERAL";
          if (!subMap[subName]) {
            subMap[subName] = {
              sortIndex: Number(item.categorySortIndex) || 999,
              rows: [],
            };
          }
          subMap[subName].rows.push({
            optionName: item.optionName || "",
            rating: item.rating,
            optionSortIndex: Number(item.optionSortIndex) || 0,
          });
        });

        const subNames = Object.keys(subMap).sort(
          (a, b) => subMap[a].sortIndex - subMap[b].sortIndex
        );
        const blockTitle = this.whssBlockTitle(record, subNames);
        const items = [];

        subNames.forEach((subName) => {
          const rows = subMap[subName].rows
            .slice()
            .sort((a, b) => a.optionSortIndex - b.optionSortIndex);

          if (subNames.length > 1) {
            items.push({
              isSectionLabel: true,
              optionName: subName,
              rating: "",
            });
          }

          rows.forEach((row) => {
            items.push({
              optionName: row.optionName,
              rating: row.rating,
              isSectionLabel: false,
            });
          });
        });

        allCategories.push({
          key: `${String(record.subjectID || blockTitle)}-${recordIndex}`,
          title: String(blockTitle).replace(/^[\s,]+/, "").trim(),
          sortIndex:
            Number(record.sortIndex) ||
            Number(marksData[0] && marksData[0].categorySortIndex) ||
            recordIndex,
          items,
        });
      });

      allCategories.sort((a, b) => a.sortIndex - b.sortIndex);

      return { allCategories };
    },

    balanceCategoriesIntoColumns(categories) {
      const list = Array.isArray(categories) ? categories.slice() : [];
      if (!list.length) return [[], []];

      const weightOf = (category) =>
        ((category.items && category.items.length) || 0) + 1;

      const ordered = list.slice().sort((a, b) => weightOf(b) - weightOf(a));
      const columns = [[], []];
      const weights = [0, 0];

      ordered.forEach((category) => {
        const target = weights[0] <= weights[1] ? 0 : 1;
        columns[target].push(category);
        weights[target] += weightOf(category);
      });

      const byOriginal = (a, b) => {
        const si =
          (Number(a.sortIndex) || 999) - (Number(b.sortIndex) || 999);
        if (si !== 0) return si;
        return String(a.title || "").localeCompare(String(b.title || ""));
      };
      columns[0].sort(byOriginal);
      columns[1].sort(byOriginal);
      return columns;
    },

    val(value) {
      if (value === null || value === undefined || value === "") return "—";
      return String(value);
    },

    formatMark(value) {
      if (value === null || value === undefined || value === "") return "";
      return String(value).trim();
    },

    print() {
      window.print();
    },

    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
$paper: #ffffff;
$line: #dee2e6;
$line-strong: #b0b0b0;
$line-ink: #333333;
$ink: #2d3436;
$black: #000000;
$ui-font: "Outfit", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
$report-font: "Segoe UI", "Helvetica Neue", Arial, sans-serif;
$table-head-bg: #e8eaed;

.whss-early-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: $ui-font;
  color: $ink;
}

.no-print.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 2rem;
  margin: 0 auto 2rem;
  max-width: 210mm;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: sticky;
  top: 1rem;
  z-index: 1000;

  h4 {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0;
    color: #444;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  padding: 10px 24px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: #fff;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: #fff;
  border: 1px solid #dfe6e9;
  color: #636e72;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    transform: translateY(-1px);
  }
}

.state-panel {
  text-align: center;
  padding: 2.75rem 2rem;
  background: #fff;
  border-radius: 16px;
  max-width: 420px;
  margin: 3rem auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.report-stack {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  align-items: center;
}

.report-sheet {
  width: 210mm;
  max-width: 100%;
  margin: 0 auto;
  page-break-after: always;
  box-sizing: border-box;
  transition: box-shadow 0.3s ease;

  &:last-child {
    page-break-after: auto;
  }

  &__frame {
    min-height: calc(297mm - 8px);
    height: auto;
    background: $paper;
    border: 1.5px solid $line-strong;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
    box-sizing: border-box;
    overflow: visible;
    border-radius: 2px;
  }

  &__body {
    min-height: calc(297mm - 12px);
    height: auto;
    display: flex;
    flex-direction: column;
    padding: 7mm 8mm 6mm;
    box-sizing: border-box;
    font-family: $report-font;
    font-size: 12px;
    line-height: 1.45;
    color: $black;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  &__footer {
    margin-top: auto;
    flex-shrink: 0;
    padding-top: 12px;
  }
}

.letterhead {
  text-align: center;
  margin-bottom: 14px;
  flex-shrink: 0;
  padding-bottom: 10px;
  border-bottom: 1px solid $line-ink;

  &__logo-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 14px;
  }

  &__logo {
    height: 88px;
    width: auto;
    max-width: 320px;
    object-fit: contain;
    display: block;
    margin: 0 auto;
  }

  &__titles {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 6px;
  }

  &__term-box {
    width: fit-content;
    max-width: 92%;
    box-sizing: border-box;
    border: 1.5px solid $black;
    text-align: center;
    font-weight: 800;
    font-size: 12px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: $black;
    padding: 7px 26px;
    line-height: 1.35;
    background: #fff;
  }

  &__class-box {
    width: fit-content;
    min-width: 0;
    max-width: 92%;
    box-sizing: border-box;
    border: 1.5px solid $black;
    text-align: center;
    font-weight: 800;
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: $black;
    padding: 6px 32px;
    line-height: 1.35;
    background: #fff;
  }

  &__session {
    margin: 4px 0 0;
    padding: 0;
    font-size: 12px;
    font-weight: 800;
    color: $black;
    letter-spacing: 0.03em;
    text-align: center;
  }
}

.student-info-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-bottom: 12px;
  padding-bottom: 12px;
  flex-shrink: 0;
  font-size: 12px;
  line-height: 1.4;
  color: $black;

  td {
    padding: 5px 8px 5px 0;
    vertical-align: middle;
    border: none;
  }

  &__col-label {
    width: 124px;
  }

  &__col-roll-label {
    width: 56px;
  }

  &__col-roll {
    width: 72px;
  }

  &__label {
    font-weight: 800;
    white-space: nowrap;
    vertical-align: middle;
    padding-right: 10px;
    letter-spacing: 0.01em;

    &--roll {
      padding-left: 6px;
      padding-right: 8px;
      text-align: right;
    }
  }

  &__age-group {
    display: grid;
    grid-template-columns: auto 36px auto 36px;
    align-items: center;
    column-gap: 10px;
    row-gap: 4px;
    max-width: 240px;
  }

  &__age-label {
    font-weight: 700;
  }

  &__box {
    display: block;
    box-sizing: border-box;
    border: 1.5px solid $black;
    background: #fff;
    font-weight: 700;
    min-height: 26px;
    line-height: 1.4;
    padding: 4px 10px;

    &--name {
      width: fit-content;
      min-width: 128px;
      max-width: 100%;
      white-space: nowrap;
    }

    &--roll {
      width: 100%;
      text-align: center;
      padding: 4px 6px;
    }
  }

  &__mini {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 24px;
    border: 1.5px solid $black;
    padding: 0 7px;
    font-weight: 800;
    font-size: 11.5px;
    box-sizing: border-box;
    background: #fff;
  }

  &__signature {
    width: 80px;
    vertical-align: bottom;
    padding: 0 0 6px 10px;
  }

  &__signature-line {
    border-bottom: 1.5px solid $black;
    min-height: 30px;
    width: 100%;
  }
}

.report-body {
  flex: 1 1 auto;
  min-height: 0;
  padding-top: 4px;
}

.subjects-wrap {
  display: flex;
  gap: 8px;
  min-width: 0;
  width: 100%;

  &--full {
    width: 100%;
  }

  &__col {
    flex: 1;
    min-width: 0;
  }
}

.subject-block {
  margin-bottom: 6px;
}

.subject-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;
  table-layout: fixed;

  th,
  td {
    border: 1px solid $black;
    padding: 3px 6px;
    vertical-align: middle;
    color: $black;
    line-height: 1.35;
  }

  &__title {
    text-align: center;
    font-weight: 800;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: $table-head-bg;
    color: $black;
    padding: 5px 6px;
    line-height: 1.3;
    border-color: $black;
  }

  &__section-label {
    font-weight: 800;
    font-size: 10px;
    padding: 4px 6px;
    background: #f3f4f6;
    color: $black;
  }

  &__section-row .subject-table__section-label {
    border-top-width: 0;
  }

  &__criteria {
    width: 86%;
    line-height: 1.35;
    font-weight: 500;
  }

  &__mark {
    width: 14%;
    text-align: center;
    font-weight: 800;
    font-size: 10.5px;
  }
}

.comments {
  margin-top: 10px;
  font-size: 12px;
  border: 1.5px solid $black;
  page-break-inside: avoid;

  &__label {
    font-weight: 800;
    padding: 6px 10px 5px;
    color: $black;
    border-bottom: 1px solid $line-strong;
    letter-spacing: 0.02em;
  }

  &__body {
    min-height: 92px;
    padding: 8px 10px 10px;
  }

  &__text {
    margin: 0;
    min-height: 76px;
    line-height: 1.5;
    font-size: 11.5px;
    font-weight: 600;
    color: $black;
    white-space: pre-wrap;
  }
}

.grading-key-footer {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 11.5px;
  margin-bottom: 0;
  page-break-inside: avoid;

  th,
  td {
    border: 1.5px solid $black;
    color: $black;
    vertical-align: middle;
  }

  &__heading {
    text-align: center;
    font-weight: 800;
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 6px 10px;
    background: #fff;
  }

  &__item {
    text-align: center;
    padding: 6px 6px;
    font-size: 11px;
    line-height: 1.35;
  }

  &__code {
    font-weight: 800;
    margin-right: 5px;
  }

  &__label {
    font-weight: 700;
  }
}

.signatures {
  display: flex;
  justify-content: space-between;
  margin-top: 14px;
  gap: 16px;
  page-break-inside: avoid;

  &__cell {
    flex: 1;
    text-align: center;
  }

  &__line {
    border-bottom: 1.5px solid $black;
    height: 26px;
    margin-bottom: 6px;
  }

  &__label {
    font-size: 11.5px;
    font-weight: 700;
    color: $black;
    letter-spacing: 0.02em;
  }
}

@page {
  size: A4 portrait;
  margin: 3mm 4mm;
}

@media print {
  html,
  body,
  .whss-early-report,
  .report-sheet,
  .report-sheet__frame,
  .report-sheet__body,
  table,
  th,
  td {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }

  .no-print {
    display: none !important;
  }

  .whss-early-report {
    background: #fff !important;
    padding: 0 !important;
    min-height: auto !important;
  }

  .report-stack {
    gap: 0 !important;
  }

  .report-sheet {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    page-break-after: always;
    break-after: page;

    &:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }

    &__frame {
      min-height: calc(297mm - 6mm) !important;
      height: auto !important;
      box-shadow: none !important;
      border: 1.5px solid $line-strong !important;
      -webkit-box-decoration-break: clone;
      box-decoration-break: clone;
    }

    &__body {
      min-height: calc(297mm - 8mm) !important;
      padding: 2.5mm 5mm 4mm !important;
      font-size: 12px !important;
    }
  }

  .letterhead {
    border-bottom-color: $black !important;
  }

  .subject-table__title {
    background: $table-head-bg !important;
  }

  .letterhead,
  .student-info-table,
  .grading-key-footer,
  .comments,
  .signatures {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .report-body,
  .subjects-wrap,
  .subject-block,
  .subject-table {
    page-break-inside: auto;
    break-inside: auto;
  }
}
</style>
